<template>
  <div>
    <n-loading-spinner v-if="loading" />
    <CardRenderer
      v-if="!loading"
      :colour="'#062B80'"
      :location="'embedded'"
      :cards="getCards()"
      @submit="cardSubmit($event)"
    />
  </div>
</template>

<script lang="ts" setup>
import CardRenderer from '@/shared/components/cardRenderer/CardRenderer.vue';
import { Card, CardSubmitEvent, CardSubmitParsedValue } from '@/shared/components/cardRenderer';
import { InstalledApp, AppConfigViewModel } from '@/features/apps/shared/models';
import Toast from '@/shared/services/toast';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import { ConfigRequest } from './configRequest';
import { AppCard } from '@/features/apps/shared/models/appCard';
import { computed, onMounted, PropType, ref } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  installedApp: {
    type: Object as PropType<InstalledApp>,
    required: true,
  },
  configRequest: {
    type: Function as PropType<
      (workspaceId: string, installedAppId: string, values: any, storedData: any, componentId: any) => Promise<any>
    >,
    required: true,
  },
});

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);

const card = ref<AppCard>({
  components: [],
});
const loading = ref(true);

onMounted(() => appConfig(props.installedApp.installedAppId));

function getCards(): Card[] {
  return [
    {
      id: 'appConfig',
      content: card.value,
    },
  ];
}

function cardSubmit(e: CardSubmitEvent) {
  appConfig(props.installedApp.installedAppId, e.parsedValues, e.componentId);
}

const emit = defineEmits<{
  (e: 'configured', installedAppId: string, config?: any): void;
}>();

async function appConfig(installedAppId: string, values?: CardSubmitParsedValue[], componentId?: string) {
  try {
    const res: AppConfigViewModel = await props.configRequest(
      currentWorkspace.value.id,
      installedAppId,
      getInputData(values),
      storedData.value,
      componentId,
    );
    if (res.card && res.card.components) {
      // more UI to render
      card.value = res.card;
      loading.value = false;
    } else {
      // config endpoint has created a new configuration
      Toast.showSuccess('Configured app');
      emit('configured', installedAppId, res.config);
    }
  } catch (error: any) {
    Toast.showError(error.message);
    if (!card.value) {
      card.value = {
        components: [],
      };
    }
    loading.value = false;
  }
}

function getInputData(submittedValues?: CardSubmitParsedValue[]): { [key: string]: string } {
  const values: { [key: string]: string } = {};
  if (submittedValues) {
    for (const value of submittedValues) {
      values[value.id] = value.value;
    }
  }
  return values;
}

const storedData = computed(() => {
  if (card.value.storedData) {
    return card.value.storedData;
  }
  return {};
});
</script>
